export type TargetService = {
  id: string;
  name: string;
  port: number;
  capability: 'terminal' | 'desktop';
};

export type Target = {
  id: string;
  name: string;
  alias: string;
  online: boolean;
  services: TargetService[];
};

export type ContractorOrganization = {
  id: string;
  name: string;
};

export type AgreementSection = {
  heading: string;
  paragraphs: string[];
};

export type ContractorAgreementDocument = {
  version: string;
  effectiveDate: string;
  title: string;
  subtitle: string;
  signatureStatement: string;
  sections: AgreementSection[];
  hash: string;
};

export type AgreementStatus = {
  version: string;
  acceptedAt: string | null;
  typedLegalName: string | null;
};

export type ContractorProfile = {
  id: string;
  email: string;
  name: string | null;
  agreement: AgreementStatus;
  organizations: ContractorOrganization[];
};

export type ClipboardAccess = 'off' | 'pasteOnly' | 'twoWay';

export type SessionWatermarking = {
  enabled: boolean;
  showContractorIdentity: boolean;
  showSessionDuration: boolean;
};

export type SessionLimits = {
  /** Zero runs a session until it is ended by hand. */
  sessionTimeLimitMinutes: number;
  /** Zero never cuts an idle session. */
  idleTimeoutMinutes: number;
  warningBeforeEndSeconds: number;
  maxConcurrentSessions: number;
  recordingRetentionDays: number;
};

export type SessionSecurityControl = {
  clipboardAccess: ClipboardAccess;
  enableSessionRecording: boolean;
  enableFileTransfer: boolean;
  watermarking: SessionWatermarking;
  sessionLimits: SessionLimits;
};

export type SessionGrant = {
  capability: 'terminal' | 'desktop';
  host: string;
  port: number;
  proxy: { host: string; port: number; username: string };
  policy: SessionSecurityControl;
};

const API = import.meta.env.VITE_ZERAC_API_URL ?? 'https://api.zerac.info';
const ORGANIZATION_HEADER = 'x-zerac-organization';

async function request<T>(
  path: string,
  token: string,
  organizationId?: string,
  init?: RequestInit
): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  if (organizationId) {
    headers[ORGANIZATION_HEADER] = organizationId;
  }

  let response: Response;
  try {
    response = await fetch(`${API}${path}`, { ...init, headers });
  } catch {
    throw new Error('Could not reach Zerac. Check your connection and try again.');
  }

  if (!response.ok) {
    const body = await response.text();
    let message = body || response.statusText;
    try {
      const parsed = JSON.parse(body);
      message = parsed.message ?? message;
    } catch {
      message = body || response.statusText;
    }

    if (response.status === 401) {
      message = 'Your session has expired. Sign in again to continue.';
    }
    if (response.status === 403) {
      message = message || 'You do not have access to that organization.';
    }

    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

export function streamUrl(organizationId: string, resourceId: string, port: number): string {
  const base = API.replace(/^http/, 'ws');
  const query = new URLSearchParams({
    organization: organizationId,
    resource: resourceId,
    port: String(port),
  });
  return `${base}/contractors/stream?${query.toString()}`;
}

export function streamProtocols(token: string): string[] {
  return ['zerac.session', `zerac.token.${token}`];
}

export function fetchProfile(token: string): Promise<ContractorProfile> {
  return request<ContractorProfile>('/contractors/me', token);
}

export function fetchAgreement(
  token: string
): Promise<{ document: ContractorAgreementDocument; status: AgreementStatus }> {
  return request<{ document: ContractorAgreementDocument; status: AgreementStatus }>(
    '/contractors/agreement',
    token
  );
}

export function acceptAgreement(
  token: string,
  version: string,
  typedLegalName: string
): Promise<AgreementStatus> {
  return request<AgreementStatus>('/contractors/agreement/accept', token, undefined, {
    method: 'POST',
    body: JSON.stringify({ version, typedLegalName }),
  });
}

export async function fetchTargets(token: string, organizationId: string): Promise<Target[]> {
  const { targets } = await request<{ targets: Target[] }>(
    `/contractors/targets?organizationId=${encodeURIComponent(organizationId)}`,
    token,
    organizationId
  );
  return targets;
}

export function fetchSessionPolicy(
  token: string,
  organizationId: string
): Promise<SessionSecurityControl> {
  return request<SessionSecurityControl>(
    `/contractors/policy?organizationId=${encodeURIComponent(organizationId)}`,
    token,
    organizationId
  );
}

export function createSession(
  token: string,
  organizationId: string,
  resourceId: string,
  port: number,
  acknowledgedRecording = false
): Promise<SessionGrant> {
  return request<SessionGrant>('/contractors/sessions', token, organizationId, {
    method: 'POST',
    body: JSON.stringify({ organizationId, resourceId, port, acknowledgedRecording }),
  });
}
